'use client';

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import { createClient } from '@/lib/local-api/client';
import { notificationsApi } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { BellIcon, ExclamationTriangleIcon, ChatBubbleLeftEllipsisIcon, BookmarkIcon, MegaphoneIcon, CheckIcon, TrashIcon, ArrowPathIcon, HeartIcon, EyeIcon, ShieldExclamationIcon, StarIcon,  } from '@heroicons/react/24/outline';
import { BellIcon as BellSolid } from '@heroicons/react/24/solid';
import Icon from '@/components/ui/AppIcon';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';


interface NotificationItem {
  id: string;
  recipient_id: string;
  actor_id: string | null;
  post_id: string | null;
  notification_type: string;
  is_read: boolean;
  created_at: string;
  metadata?: {
    title?: string;
    message?: string;
    project_name?: string;
    project_id?: string;
    scam_type?: string;
    severity?: string;
    preview?: string;
  } | null;
  actor?: { full_name: string } | null;
  post?: { content: string } | null;
}

type FilterTab = 'all' | 'scam_alert' | 'community_reply' | 'watchlist_update' | 'like' | 'comment' | 'admin_alert';

const typeConfig: Record<string, {
  icon: React.ElementType;
  color: string;
  bg: string;
  label: string;
  tabLabel: string;
}> = {
  scam_alert: {
    icon: ShieldExclamationIcon,
    color: 'text-red-400',
    bg: 'bg-red-400/10',
    label: 'Scam Alert',
    tabLabel: 'Scam Alerts',
  },
  community_reply: {
    icon: ChatBubbleLeftEllipsisIcon,
    color: 'text-primary',
    bg: 'bg-primary/10',
    label: 'Community Reply',
    tabLabel: 'Replies',
  },
  watchlist_update: {
    icon: StarIcon,
    color: 'text-amber-400',
    bg: 'bg-amber-400/10',
    label: 'Watchlist Update',
    tabLabel: 'Watchlist',
  },
  like: {
    icon: HeartIcon,
    color: 'text-pink-400',
    bg: 'bg-pink-400/10',
    label: 'Post Like',
    tabLabel: 'Likes',
  },
  comment: {
    icon: ChatBubbleLeftEllipsisIcon,
    color: 'text-blue-400',
    bg: 'bg-blue-400/10',
    label: 'Comment',
    tabLabel: 'Comments',
  },
  bookmark: {
    icon: BookmarkIcon,
    color: 'text-violet-400',
    bg: 'bg-violet-400/10',
    label: 'Bookmarked',
    tabLabel: 'Bookmarks',
  },
  project_warning: {
    icon: ExclamationTriangleIcon,
    color: 'text-orange-400',
    bg: 'bg-orange-400/10',
    label: 'Project Warning',
    tabLabel: 'Warnings',
  },
  admin_alert: {
    icon: MegaphoneIcon,
    color: 'text-red-400',
    bg: 'bg-red-400/10',
    label: 'Admin Alert',
    tabLabel: 'Admin',
  },
};

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function getNotificationText(n: NotificationItem): { title: string; body: string } {
  const cfg = typeConfig[n.notification_type];
  const actorName = n.actor?.full_name || 'Someone';

  switch (n.notification_type) {
    case 'scam_alert':
      return {
        title: n.metadata?.title || 'New Scam Alert',
        body: n.metadata?.message || 'A new scam report matching your interests was filed.',
      };
    case 'community_reply':
      return {
        title: `${actorName} replied to your post`,
        body: n.metadata?.preview || n.post?.content?.slice(0, 80) || 'View the reply in the community.',
      };
    case 'watchlist_update':
      return {
        title: `${n.metadata?.project_name || 'A project'} was updated`,
        body: n.metadata?.message || 'A project on your watchlist has new information.',
      };
    case 'like':
      return {
        title: `${actorName} liked your post`,
        body: n.post?.content?.slice(0, 80) || 'View your post.',
      };
    case 'comment':
      return {
        title: `${actorName} commented on your post`,
        body: n.post?.content?.slice(0, 80) || 'View the comment.',
      };
    case 'bookmark':
      return {
        title: `${actorName} saved your post`,
        body: n.post?.content?.slice(0, 80) || 'Your post was bookmarked.',
      };
    case 'project_warning':
      return {
        title: n.metadata?.title || 'Project Warning',
        body: n.metadata?.message || 'A project you follow has a new warning.',
      };
    case 'admin_alert':
      return {
        title: n.metadata?.title || 'Platform Announcement',
        body: n.metadata?.message || 'An important update from the admin team.',
      };
    default:
      return { title: cfg?.label || 'Notification', body: 'You have a new notification.' };
  }
}

const FILTER_TABS: { key: FilterTab; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'scam_alert', label: 'Scam Alerts' },
  { key: 'community_reply', label: 'Replies' },
  { key: 'watchlist_update', label: 'Watchlist' },
  { key: 'like', label: 'Likes' },
  { key: 'admin_alert', label: 'Admin' },
];

export default function NotificationCenterContent() {
  const { user } = useAuth();
  const router = useRouter();
  const backButton = (
    <button
      type="button"
      onClick={() => {
        if (window.history.length > 1) router.back();
        else router.replace('/community-network');
      }}
      className="inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-semibold text-primary hover:bg-primary/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary"
      aria-label="Go back from notifications"
    >
      <ArrowLeftIcon className="h-5 w-5" aria-hidden="true" />
      Back
    </button>
  );
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState<FilterTab>('all');
  const [markingAll, setMarkingAll] = useState(false);
  const channelRef = useRef<ReturnType<ReturnType<typeof createClient>['channel']> | null>(null);
  const instanceId = useRef(Math.random().toString(36).slice(2));

  const fetchNotifications = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    const { data } = await notificationsApi.list({ limit: 60 });
    if (data) {
      const rows = Array.isArray(data) ? data : (data as any).data ?? [];
      setNotifications(rows as NotificationItem[]);
    }
    setLoading(false);
  }, [user]);

  useEffect(() => {
    if (user) fetchNotifications();
  }, [user, fetchNotifications]);

  // Real-time subscription
  useEffect(() => {
    if (!user) return;
    const supabase = createClient();
    const channelName = `notif_center_${user.id}_${instanceId.current}`;

    if (channelRef.current) {
      supabase.removeChannel(channelRef.current);
      channelRef.current = null;
    }

    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `recipient_id=eq.${user.id}`,
        },
        () => fetchNotifications()
      )
      .subscribe();

    channelRef.current = channel;
    return () => {
      supabase.removeChannel(channel);
      channelRef.current = null;
      instanceId.current = Math.random().toString(36).slice(2);
    };
  }, [user?.id, fetchNotifications]);

  const markAsRead = async (id: string) => {
    await notificationsApi.markRead(id);
    setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, is_read: true } : n)));
  };

  const deleteNotification = async (id: string) => {
    await notificationsApi.delete(id);
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  };

  const markAllRead = async () => {
    if (!user) return;
    setMarkingAll(true);
    await notificationsApi.markAllRead();
    setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
    setMarkingAll(false);
  };

  const handleNotificationClick = async (n: NotificationItem) => {
    if (!n.is_read) await markAsRead(n.id);
    if (n.notification_type === 'watchlist_update' && n.metadata?.project_id) {
      router.push(`/project/${n.metadata.project_id}`);
    } else if (n.post_id && ['like', 'comment', 'bookmark', 'community_reply'].includes(n.notification_type)) {
      router.push('/community-network');
    } else if (n.notification_type === 'scam_alert') {
      router.push('/scam-alert-center');
    }
  };

  const filtered = activeFilter === 'all'
    ? notifications
    : notifications.filter((n) => n.notification_type === activeFilter);

  const unreadCount = notifications.filter((n) => !n.is_read).length;

  if (!user) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4 text-center px-4">
        <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center">
          <BellIcon className="text-primary" style={{ width: 32, height: 32 }} />
        </div>
        {backButton}
        <h2 className="text-xl font-bold text-foreground">Sign in to view notifications</h2>
        <p className="text-muted-foreground text-sm max-w-xs">
          Get live alerts for scam reports, community replies, and watchlist updates.
        </p>
        <Link
          href="/login"
          className="px-6 py-2.5 rounded-xl text-sm font-semibold text-white gradient-blue hover:opacity-90 transition-opacity"
          style={{ boxShadow: '0 2px 12px rgba(59,110,245,0.35)' }}
        >
          Sign In
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      <div className="mb-3 pt-2 flex items-center justify-between gap-2">
        {backButton}
      </div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary/15 flex items-center justify-center">
            <BellSolid className="text-primary" style={{ width: 20, height: 20 }} />
          </div>
          <div>
            <h1 className="text-xl font-bold text-foreground leading-tight">Notifications</h1>
            {unreadCount > 0 && (
              <p className="text-xs text-muted-foreground">
                {unreadCount} unread alert{unreadCount !== 1 ? 's' : ''}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={fetchNotifications}
            className="p-2 rounded-xl hover:bg-muted text-muted-foreground hover:text-foreground transition-all"
            title="Refresh"
          >
            <ArrowPathIcon style={{ width: 17, height: 17 }} />
          </button>
          {unreadCount > 0 && (
            <button
              onClick={markAllRead}
              disabled={markingAll}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold text-primary border border-primary/30 hover:bg-primary/10 transition-all disabled:opacity-50"
            >
              <CheckIcon style={{ width: 13, height: 13 }} />
              {markingAll ? 'Marking...' : 'Mark all read'}
            </button>
          )}
        </div>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-1.5 overflow-x-auto pb-2 mb-5 scrollbar-none">
        {FILTER_TABS.map((tab) => {
          const count = tab.key === 'all'
            ? notifications.filter((n) => !n.is_read).length
            : notifications.filter((n) => n.notification_type === tab.key && !n.is_read).length;
          return (
            <button
              key={tab.key}
              onClick={() => setActiveFilter(tab.key)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold whitespace-nowrap transition-all flex-shrink-0 ${
                activeFilter === tab.key
                  ? 'text-white gradient-blue' :'text-muted-foreground bg-muted hover:text-foreground'
              }`}
              style={activeFilter === tab.key ? { boxShadow: '0 2px 10px rgba(59,110,245,0.3)' } : {}}
            >
              {tab.label}
              {count > 0 && (
                <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${
                  activeFilter === tab.key ? 'bg-white/20 text-white' : 'bg-red-500 text-white'
                }`}>
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Notification list */}
      {loading ? (
        <div className="space-y-3">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="rounded-2xl bg-card border border-border/50 p-4 animate-pulse">
              <div className="flex gap-3">
                <div className="w-10 h-10 rounded-xl bg-muted flex-shrink-0" />
                <div className="flex-1 space-y-2">
                  <div className="h-3.5 bg-muted rounded w-3/4" />
                  <div className="h-3 bg-muted rounded w-full" />
                  <div className="h-2.5 bg-muted rounded w-1/4" />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 gap-3 text-center">
          <div className="w-14 h-14 rounded-2xl bg-muted flex items-center justify-center">
            <BellIcon className="text-muted-foreground" style={{ width: 26, height: 26 }} />
          </div>
          <p className="text-sm font-semibold text-foreground">No notifications yet</p>
          <p className="text-xs text-muted-foreground max-w-xs">
            {activeFilter === 'all' ? "You'll receive alerts for scam reports, community replies, and watchlist updates here."
              : `No ${FILTER_TABS.find((t) => t.key === activeFilter)?.label?.toLowerCase()} notifications yet.`}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((n) => {
            const cfg = typeConfig[n.notification_type] || typeConfig['admin_alert'];
            const Icon = cfg.icon;
            const { title, body } = getNotificationText(n);
            return (
              <div
                key={n.id}
                onClick={() => handleNotificationClick(n)}
                className={`group relative flex gap-3 p-4 rounded-2xl border cursor-pointer transition-all duration-200 hover:border-primary/30 ${
                  n.is_read
                    ? 'bg-card border-border/40 hover:bg-muted/30' :'bg-card border-primary/20 hover:bg-primary/5'
                }`}
                style={!n.is_read ? { boxShadow: '0 0 0 1px rgba(59,110,245,0.1)' } : {}}
              >
                {/* Unread dot */}
                {!n.is_read && (
                  <span className="absolute top-4 right-4 w-2 h-2 rounded-full bg-primary" />
                )}

                {/* Icon */}
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${cfg.bg}`}>
                  <Icon className={cfg.color} style={{ width: 18, height: 18 }} />
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0 pr-6">
                  <p className={`text-sm font-semibold leading-snug mb-0.5 ${n.is_read ? 'text-foreground/80' : 'text-foreground'}`}>
                    {title}
                  </p>
                  <p className="text-xs text-muted-foreground leading-relaxed line-clamp-2">
                    {body}
                  </p>
                  <div className="flex items-center gap-2 mt-1.5">
                    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-md ${cfg.bg} ${cfg.color}`}>
                      {cfg.label}
                    </span>
                    <span className="text-[10px] text-muted-foreground/60">{timeAgo(n.created_at)}</span>
                  </div>
                </div>

                {/* Actions (shown on hover) */}
                <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  {!n.is_read && (
                    <button
                      onClick={(e) => { e.stopPropagation(); markAsRead(n.id); }}
                      className="p-1.5 rounded-lg hover:bg-primary/10 text-muted-foreground hover:text-primary transition-all"
                      title="Mark as read"
                    >
                      <EyeIcon style={{ width: 14, height: 14 }} />
                    </button>
                  )}
                  <button
                    onClick={(e) => { e.stopPropagation(); deleteNotification(n.id); }}
                    className="p-1.5 rounded-lg hover:bg-red-500/10 text-muted-foreground hover:text-red-400 transition-all"
                    title="Delete"
                  >
                    <TrashIcon style={{ width: 14, height: 14 }} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Footer info */}
      {filtered.length > 0 && (
        <p className="text-center text-[11px] text-muted-foreground/50 mt-6">
          Showing {filtered.length} notification{filtered.length !== 1 ? 's' : ''}
          {activeFilter !== 'all' ? ` · ${FILTER_TABS.find((t) => t.key === activeFilter)?.label}` : ''}
        </p>
      )}
    </div>
  );
}
